import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../BaseController';
import { FragmentNames } from '../../codegen';
import { Inject } from '@odinjs/odin';
import { ContextualBarAPI, DeckAPI, FetchAPI, ModalService } from '@philips-internal/oswald-ui-components';
import { ClickEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';

import UserContext from '../../utils/user-context';
import { CardContainerAPI, MapperAPI, ModalProps } from '@philips-internal/oswald-ui-components/lib/components';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { TeleconsultationCardType, TeleconsultationRecordsIcons, TeleconsultationRecordsLabels } from '../../constants/my-chart-teleconsultation-types';
import { MyChartTelehealthRecordsAliases } from '../../../src/codegen/constants/aliases/my-chart-telehealth-records-aliases';
import { isEmptyString, isMobile } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { DeckTelehealthRecordsProps, MapTelehealthRecordsData } from '../../constants/my-chart-teleconsultation-interface';

@Controller({fragment: FragmentNames.MY_CHART_TELEHEALTH_RECORDS})
export default class MyChartTeleconsultationRecordsController extends BaseController {

  @Inject(MyChartTelehealthRecordsAliases.FETCH_TELEHEALTH_INFO)
    fetchTeleconsultationAPI!: FetchAPI;

  @Inject(MyChartTelehealthRecordsAliases.DECK_ITEMS_RECORDS)
    deckTeleconsultationAPI!: DeckAPI;

  @Inject(MyChartTelehealthRecordsAliases.MAPPER_TELEHEALTH_RECORDS)
    mapperTeleconsultationAPI!: MapperAPI;

  @Inject(MyChartTelehealthRecordsAliases.CONTEXTUAL_BAR_TELEHEALTH_RECORDS)
    contextualBarApi!: ContextualBarAPI;

  @OnMount({ target: MyChartTelehealthRecordsAliases.MAPPER_TELEHEALTH_RECORDS })
  showDeckRecordsLoading(event: MountEvent): void {
    const mapper = event.target as MapperAPI;

    HttpService.get(APPLICATION_ENDPOINTS.GET_TELECONSULTATION_RECORDS, { 'patientCode': UserContext.patientId })
      .then(response => {
        const body = response.body as DeckTelehealthRecordsProps[];

        body.forEach(item => {
          item.deckTitle = this.getDeckTitle(item);
          item.image = this.getAvatarImage(item.registryType);
          item.registryType = this.getRegistryType(item.registryType, item.fileLink);
        });

        mapper.setData(body);
      })
  }

  @OnClick(MyChartTelehealthRecordsAliases.CARD_CONTAINER_TELEHEALTH)
  onClickCardDynamicAction(event: ClickEvent): void {
    const cardContainerAPI = event.target as CardContainerAPI;
    const data = cardContainerAPI.getData() as unknown as MapTelehealthRecordsData;

    this.openDocumentTeleconsultationRecord(data);
  }

  private getAvatarImage(typeDocument: string): string {
    switch(typeDocument){
      case TeleconsultationCardType.CHAT:
        return TeleconsultationRecordsIcons.CHAT;
      case TeleconsultationCardType.DOCUMENT:
        return TeleconsultationRecordsIcons.DOCUMENT;
      case TeleconsultationCardType.VIDEO:
        return TeleconsultationRecordsIcons.VIDEO;
      default :
        return '';
    }
  }

  private getRegistryType(typeDocument: string, fileLink: string): string {
    switch(typeDocument){
      case TeleconsultationCardType.CHAT:
        return MessageService.translate(TeleconsultationRecordsLabels.CHAT);
      case TeleconsultationCardType.DOCUMENT:
        return this.getDocumentNameByFilePath(fileLink);
      case TeleconsultationCardType.VIDEO:
        return MessageService.translate(TeleconsultationRecordsLabels.VIDEO);
      default :
        return '';
    }
  }

  private getDeckTitle(record: DeckTelehealthRecordsProps): string {
    const data = moment(record.appointmentDate).format('DD MMMM YYYY');

    return `${data} - ${record.specialty}, ${record.doctorName}`;
  }

  private openDocumentTeleconsultationRecord = (teleconsultationRecordData: MapTelehealthRecordsData): void => {
    const modalProps: ModalProps = {
      className: FragmentNames.MY_CHART_VIEW_TELECONSULTATION_RECORD,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { teleconsultationData: teleconsultationRecordData },
      focusOnOpen: false,
      fragment: FragmentNames.MY_CHART_VIEW_TELECONSULTATION_RECORD,
      onClose: () => { return; },
      title: teleconsultationRecordData.registryType,
      uuid: ''
    }

    if(isMobile()) {
      ModalService.openFullscreen(modalProps);
      return;
    }

    ModalService.openDefault(modalProps);
  }

  private getDocumentNameByFilePath = (filePath: string): string => {
    const genericFileName = MessageService.translate(TeleconsultationRecordsLabels.DOCUMENT);

    if (!filePath) {
      return genericFileName;
    }

    const fileNameArray = filePath.split('?');
    const fileNameWithExtension = fileNameArray[fileNameArray.length - 1];

    return isEmptyString(fileNameWithExtension) ? genericFileName : fileNameWithExtension;
  }
}
